// Section schemas — plan §5 + §11.2.

import { z } from "zod";
import { QuestionSchema } from "./questions.js";
import { ApplicablePeriodsSchema, PeriodType } from "./period.js";

export const SectionSchema = z.object({
  id: z.string().uuid(),
  frameworkVersionId: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  order: z.number().int(),
  sectionPrompt: z.string(),
  // Raporlama dönemi (K7): bölümde TAVAN — DB her zaman en az bir dönem tutar (default hepsi).
  applicablePeriods: z.array(PeriodType),
  // Kit v1.1 / TODO-IMPORT-02 — bölümün raporlarda geçen başlık varyantları (eş anlamlılar).
  mappingHints: z.array(z.string()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Section = z.infer<typeof SectionSchema>;

// Tree node — section + questions (ordered)
export const SectionWithQuestionsSchema = SectionSchema.extend({
  questions: z.array(QuestionSchema),
});
export type SectionWithQuestions = z.infer<typeof SectionWithQuestionsSchema>;

export const FrameworkVersionTreeResponseSchema = z.object({
  // M-L2: framework-level L2 prompt (tüm sürümlerde ortak, her zaman editlenebilir)
  frameworkPrompt: z.string(),
  frameworkUpdatedAt: z.string().datetime(),
  // Raporlama dönemi (K6): çeyreklik proje açma izni — FormManager banner/UI bunu okur.
  supportsQuarterly: z.boolean(),
  // Bölüm kodları standart referansı mı — FormManager anahtarı bunu okur (framework düzeyi, canlı).
  sectionCodesAreStandardRefs: z.boolean(),
  version: z.object({
    id: z.string().uuid(),
    frameworkId: z.string().uuid(),
    versionNumber: z.string(),
    status: z.enum(["draft", "published", "archived"]),
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
  sections: z.array(SectionWithQuestionsSchema),
});
export type FrameworkVersionTreeResponse = z.infer<typeof FrameworkVersionTreeResponseSchema>;

export const CreateSectionRequestSchema = z.object({
  expectedVersionUpdatedAt: z.string().datetime(),
  code: z.string().min(1, "Bölüm kodu boş olamaz"),
  label: z.string().min(1, "Bölüm adı boş olamaz"),
  description: z.string().optional(),
  sectionPrompt: z.string().default(""),
  order: z.number().int().optional(),
  applicablePeriods: ApplicablePeriodsSchema.optional(),
  mappingHints: z.array(z.string().min(1).max(120)).max(30).optional(),
});
// z.input: client gönderirken default'lu alanlar optional; backend parse sonrası dolu.
export type CreateSectionRequest = z.input<typeof CreateSectionRequestSchema>;

export const UpdateSectionRequestSchema = z
  .object({
    expectedVersionUpdatedAt: z.string().datetime(),
    code: z.string().min(1).optional(),
    label: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    sectionPrompt: z.string().optional(),
    order: z.number().int().optional(),
    applicablePeriods: ApplicablePeriodsSchema.optional(),
    mappingHints: z.array(z.string().min(1).max(120)).max(30).optional(),
  })
  .refine((v) => Object.keys(v).filter((k) => k !== "expectedVersionUpdatedAt").length > 0, {
    message: "En az bir alan güncellenmeli",
  });
export type UpdateSectionRequest = z.infer<typeof UpdateSectionRequestSchema>;
